import {
	BodyScrollEndEvent,
	ColDef,
	FirstDataRenderedEvent,
	RowClassParams,
	RowClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	aggridHeaderClass,
	commonCodeCellStyle,
} from "src/components/common/common";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import {
	cellDateComparator,
	onlyTouchGridStyle,
} from "src/utils/grid/gridFunctionUtil";
import {
	defaultColDef,
	getDefaultRowIndexColumnConfig,
	restoreGridScrollState,
	saveGridScrollState,
} from "src/utils/grid/gridStyleUtils";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { formatDate } from "src/utils/string/stringUtil";
import {
	defaultKSK2040_R01HeaderDto,
	KSK2040_R01HeaderDto,
} from "./Dto_KSK2040_R01";
import { useKSK2040_R01Store } from "./Store_KSK2040_R01";

const COLUMN_STATE_KEY = "KSK2040_R01-target-grid-column-state";
const HEADER_NAMES: Record<string, string> = {
	rowIndex: "",
	compNm: "사업장",
	productDt: "생산일자",
	productResultNo: "생산실적No",
	productOrderNo: "생산지시No",
	workplaceNm: "작업장",
	jobTeamNm: "작업조",
	productEmpNm: "작업자",
	itemCd: "품목코드",
	itemNm: "품명",
	itemSpec: "규격",
	productQty: "생산수량",
	badQty: "불량수량",
	acceptQty: "양품수량",
	itemUnit: "단위",
	inspectionStatus: "검사여부",
};

const compareNumbers = (valueA: number | undefined, valueB: number | undefined) =>
	Number(valueA ?? 0) - Number(valueB ?? 0);

interface TableKSK2040R01Props {
	gridRef: React.RefObject<AgGridReact<KSK2040_R01HeaderDto> | null>;
	fetchOne: (target: KSK2040_R01HeaderDto) => Promise<void>;
}

export const Table_KSK2040_R01 = ({ gridRef, fetchOne }: TableKSK2040R01Props) => {
	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const {
		KSK2040_R01List,
		selectedKSK2040_R01,
		initSelectedKSK2040_R01,
		columnState,
		setColumnState,
		scrollTop,
		setScrollTop,
	} = useKSK2040_R01Store();
	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent<KSK2040_R01HeaderDto>({
		gridRef,
		key: "productResultNo",
		gridData: KSK2040_R01List,
		defaultGridData: defaultKSK2040_R01HeaderDto,
		selectedRow: selectedKSK2040_R01,
	});
	const getCellStyle = useCallback((params: any, key: string) =>
		onlyTouchGridStyle(key, "compNm")(params), []);

	const baseColumnDefs = useMemo<ColDef<KSK2040_R01HeaderDto>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "사업장",
			colId: "compNm",
			field: "compNm",
			width: 110,
			cellStyle: (params) => getCellStyle(params, "compNm"),
		},
		{ headerName: "생산일자", colId: "productDt", field: "productDt", width: 110,
			valueFormatter: (params) => formatDate(params.value), comparator: cellDateComparator,
			cellStyle: (params) => getCellStyle(params, "productDt") },
		{ headerName: "생산실적No", colId: "productResultNo", field: "productResultNo", width: 150,
			cellStyle: (params) => getCellStyle(params, "productResultNo") },
		{ headerName: "생산지시No", colId: "productOrderNo", field: "productOrderNo", width: 150,
			cellStyle: (params) => getCellStyle(params, "productOrderNo") },
		{ headerName: "작업장", colId: "workplaceNm", field: "workplaceNm", width: 130,
			cellStyle: (params) => getCellStyle(params, "workplaceNm") },
		{ headerName: "작업조", colId: "jobTeamNm", field: "jobTeamNm", width: 100,
			cellStyle: (params) => getCellStyle(params, "jobTeamNm") },
		{ headerName: "작업자", colId: "productEmpNm", field: "productEmpNm", width: 100,
			cellStyle: (params) => getCellStyle(params, "productEmpNm") },
		{ headerName: "품목코드", colId: "itemCd", field: "itemCd", width: 120,
			cellStyle: (params) => getCellStyle(params, "itemCd") },
		{ headerName: "품명", colId: "itemNm", field: "itemNm", width: 140,
			cellStyle: (params) => getCellStyle(params, "itemNm") },
		{ headerName: "규격", colId: "itemSpec", field: "itemSpec", width: 110,
			cellStyle: (params) => getCellStyle(params, "itemSpec") },
		{ headerName: "생산수량", colId: "productQty", field: "productQty", width: 95, type: "numericColumn",
			cellStyle: (params) => getCellStyle(params, "productQty"), comparator: compareNumbers,
			valueFormatter: (params) => Number(params.value ?? 0).toLocaleString() },
		{ headerName: "불량수량", colId: "badQty", field: "badQty", width: 95, type: "numericColumn",
			cellStyle: (params) => getCellStyle(params, "badQty"), comparator: compareNumbers,
			valueFormatter: (params) => Number(params.value ?? 0).toLocaleString() },
		{ headerName: "양품수량", colId: "acceptQty", field: "acceptQty", width: 95, type: "numericColumn",
			cellStyle: (params) => getCellStyle(params, "acceptQty"), comparator: compareNumbers,
			valueFormatter: (params) => Number(params.value ?? 0).toLocaleString() },
		{ headerName: "단위", colId: "itemUnit", field: "itemUnit", width: 70,
			cellStyle: (params) => getCellStyle(params, "itemUnit") },
		{
			headerName: "검사여부",
			colId: "inspectionStatus",
			width: 90,
			valueGetter: (params) => params.node?.rowPinned
				? ""
				: Number(params.data?.inspectionCount ?? 0) > 0 ? "검사완료" : "미검사",
			cellStyle: commonCodeCellStyle,
		},
	], [getCellStyle]);

	const columnDefs = useMemo<ColDef<KSK2040_R01HeaderDto>[]>(() =>
		GridCustomColumnDefs<KSK2040_R01HeaderDto>({
			baseColumnDefs,
			columnState: columnState?.some((state) => state.colId === "rowIndex")
				? columnState
				: null,
			headerNames: HEADER_NAMES,
			fallbackHeaderClass: aggridHeaderClass,
		}) as ColDef<KSK2040_R01HeaderDto>[], [baseColumnDefs, columnState]);

	const pinnedBottomRowData = useMemo<KSK2040_R01HeaderDto[]>(() => [{
		...defaultKSK2040_R01HeaderDto,
		compNm: `${KSK2040_R01List.length.toLocaleString()}건`,
		productDt: undefined,
		productQty: KSK2040_R01List.reduce((sum, item) => sum + Number(item.productQty || 0), 0),
		badQty: KSK2040_R01List.reduce((sum, item) => sum + Number(item.badQty || 0), 0),
		acceptQty: KSK2040_R01List.reduce((sum, item) => sum + Number(item.acceptQty || 0), 0),
	}], [KSK2040_R01List]);

	const rowClassRules = useMemo<Record<string, (params: RowClassParams<KSK2040_R01HeaderDto>) => boolean>>(() => ({
		...createRowClassRules<KSK2040_R01HeaderDto>({
			idKey: "productResultNo",
			selectedList: selectedKSK2040_R01 ? [selectedKSK2040_R01] : [],
			deletedList: [],
		}),
		hover: (params) => params.node.rowIndex === hoveredRowIndex,
	}), [createRowClassRules, hoveredRowIndex, selectedKSK2040_R01]);

	const handleRowClick = useCallback((event: RowClickedEvent<KSK2040_R01HeaderDto>) => {
		if (!event.node.rowPinned && event.data) void fetchOne(event.data);
	}, [fetchOne]);

	const handleSelectionClear = useCallback(() => {
		if (selectedKSK2040_R01) {
			void fetchOne(selectedKSK2040_R01);
			return;
		}
		initSelectedKSK2040_R01();
	}, [fetchOne, initSelectedKSK2040_R01, selectedKSK2040_R01]);

	const handleBodyScrollEnd = useCallback((event: BodyScrollEndEvent<KSK2040_R01HeaderDto>) => {
		if (event.direction === "vertical") saveGridScrollState(event.api, setScrollTop);
	}, [setScrollTop]);

	const handleFirstDataRendered = useCallback((event: FirstDataRenderedEvent<KSK2040_R01HeaderDto>) => {
		restoreGridScrollState(event.api, scrollTop);
	}, [scrollTop]);

	return (
		<GridContextWrapper<KSK2040_R01HeaderDto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={handleSelectionClear}
			defaultRowData={defaultKSK2040_R01HeaderDto}
			baseColumnDefs={columnDefs}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			enableDataHistory={false}
		>
			<Grid_Touch<KSK2040_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={rowClassRules}
				onRowClicked={handleRowClick}
				onBodyScrollEnd={handleBodyScrollEnd}
				onFirstDataRendered={handleFirstDataRendered}
				pinnedBottomRowData={pinnedBottomRowData}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
			/>
		</GridContextWrapper>
	);
};
